import React, { Fragment } from 'react';
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import { connect } from '../../redux/store';
import { Link } from '@reach/router';
import {
  FormTypeahead,
  UserTypeahead,
  StateListWrapper,
  C,
  PaginationBar,
  addToast,
  selectServicesKapp,
} from '@kineticdata/bundle-common';
import { I18n } from '@kineticdata/react';
import {
  UncontrolledDropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from 'reactstrap';
import classNames from 'classnames';
import { PageTitle } from '../shared/PageTitle';
import { RequestCard } from './RequestCard';
import {
  actions,
  searchFields,
  typeStates,
  tabLabels,
} from '../../redux/modules/requests';
import { getSubmissionPath } from '../../utils';

const SubmissionCount = ({ count }) => {
  return typeof count !== 'number' ? (
    ''
  ) : (
    <span className="ml-auto pl-2">{count >= 1000 ? '999+' : count}</span>
  );
};

/**
 * Wrapper for typeahead components to maintain the complex value object
 */
const TypeaheadWrapper = withState(
  'value',
  'setValue',
  props => props.value || null,
)(props => props.children({ value: props.value, setValue: props.setValue }));

/**
 * Overrides for the rendering of the SuggestionContent in the Typeaheads
 */
const FormTypeaheadSuggestionContent = ({ suggestion }) => (
  <>
    <div>
      <I18n>{suggestion.get('name')}</I18n>
    </div>
    <small className="text-muted">{`${suggestion.get('slug')}`}</small>
  </>
);
const UserTypeaheadSuggestionContent = ({ suggestion }) => (
  <>
    <small className="text-muted">{`${suggestion.get('username')}`}</small>
    <div>
      <I18n>{suggestion.get('displayName')}</I18n>
    </div>
  </>
);

export const RequestListComponent = props => (
  <Fragment>
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          parts={['My Requests']}
          title={'My Requests'}
          actions={[
            props.type !== 'Draft' && {
              icon: 'filter',
              className: `btn-flex ${
                props.filterOpen ? 'btn-dark btn-icon-light' : 'btn-icon-dark'
              }`,
              onClick: props.toggleFilters,
              aria: 'Toggle Filters',
              label: props.filterOpen ? 'Close' : null,
            },
            {
              icon: 'sync',
              disabled: props.filterOpen && props.filterQuery.length === 0,
              onClick: props.reloadRequests,
              aria: 'Reload Requests',
            },
          ]}
        />

        <div className="column-container">
          <div className="column-panel column-panel--small">
            <div className="side-nav">
              {props.isMobile ? (
                <UncontrolledDropdown className="side-nav__dropdown">
                  <DropdownToggle
                    color="dropdown"
                    className={classNames('side-nav__toggle')}
                  >
                    <span className="label">
                      <I18n>
                        {tabLabels.find(t => t.type === props.type)['label']}
                      </I18n>
                    </span>
                    <span
                      className={`ml-auto fa fa-fw fa-angle-down text-muted`}
                    />
                  </DropdownToggle>
                  <DropdownMenu>
                    {tabLabels
                      .filter(tab => !tab.filter || tab.filter(props.profile))
                      .map(({ type, label }) => (
                        <DropdownItem
                          key={`tab-type-${type}`}
                          tag={Link}
                          to={`${props.appLocation}/requests/${type}`}
                          className={classNames({
                            active: props.type === type,
                          })}
                        >
                          <span className="label">
                            <I18n>{label}</I18n>
                          </span>
                          <SubmissionCount count={props.counts[type]} />
                        </DropdownItem>
                      ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
              ) : (
                <>
                  {tabLabels
                    .filter(tab => !tab.filter || tab.filter(props.profile))
                    .map(({ type, label }) => (
                      <Link
                        key={`tab-type-${type}`}
                        to={`${props.appLocation}/requests/${type}`}
                        className={classNames({ active: props.type === type })}
                      >
                        <span className="label">
                          <I18n>{label}</I18n>
                        </span>
                        <SubmissionCount count={props.counts[type]} />
                      </Link>
                    ))}
                  <div className="side-nav__divider" />
                </>
              )}
              <button
                className="btn btn-link"
                onClick={props.handleSortDirectionChange(
                  props.direction === 'DESC' ? 'ASC' : 'DESC',
                )}
                titile="Toggle Sort Direction"
              >
                <span>
                  <I18n>Sorted by</I18n>{' '}
                  <strong>
                    <I18n>
                      {props.direction === 'ASC'
                        ? 'oldest first'
                        : 'newest first'}
                    </I18n>
                  </strong>
                </span>
                <span
                  className={classNames('fa fa-sm ml-1', {
                    'fa-sort-down': props.direction !== 'ASC',
                    'fa-sort-up': props.direction === 'ASC',
                  })}
                />
              </button>
            </div>
          </div>

          <div className="column-panel column-panel--large">
            {props.filterOpen && (
              <div className="filter-bar">
                <div className="filter-bar__title">Filters:</div>
                {props.filterQuery.map((q, i) => (
                  <div
                    className={classNames('filter-bar__group', {
                      'is-empty': !q.value,
                    })}
                    key={`filter-group-${q.field ? q.field.id : 'all'}`}
                  >
                    <UncontrolledDropdown>
                      <DropdownToggle
                        color="link"
                        id={`filter-group-label-${i}`}
                      >
                        <I18n>{q.field ? q.field.label : 'Any Field'}</I18n>
                      </DropdownToggle>
                      <DropdownMenu className="">
                        {props.filterQuery.length <= 1 && (
                          <DropdownItem
                            key="any-field"
                            onClick={props.handleFilterFieldChange(i, null)}
                          >
                            <em>
                              <I18n>Any Field</I18n>
                            </em>
                          </DropdownItem>
                        )}
                        {props.swapFilterOptions(q.field).map(sf => (
                          <DropdownItem
                            key={sf.id}
                            onClick={props.handleFilterFieldChange(i, sf.id)}
                          >
                            {sf.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                    <span className="equals-sign">
                      <I18n>=</I18n>
                    </span>
                    {q.field && q.field.type === 'form' ? (
                      <TypeaheadWrapper
                        value={
                          q.value ? { slug: q.value, name: q.label } : null
                        }
                      >
                        {({ value, setValue }) => (
                          <FormTypeahead
                            value={value}
                            onChange={form => {
                              setValue(form);
                              props.handleFilterValueChange(i)({
                                target: {
                                  value: form ? form.get('slug') : '',
                                  label: form ? form.get('name') : '',
                                },
                              });
                            }}
                            search={{
                              kappSlug:
                                props.servicesKapp && props.servicesKapp.slug,
                              fields: [
                                { name: 'name', operator: 'matches' },
                                {
                                  name: 'type',
                                  operator: 'in',
                                  value: [
                                    C.REQUEST_FORM_TYPE,
                                    C.UTILITY_FORM_TYPE,
                                  ],
                                },
                                {
                                  name: 'status',
                                  operator: 'in',
                                  value: C.REQUEST_FORM_STATUSES,
                                },
                              ],
                            }}
                            minSearchLength={1}
                            labelProp="name"
                            components={{
                              SuggestionContent: FormTypeaheadSuggestionContent,
                            }}
                            aria-labelledby={`filter-group-label-${i}`}
                          />
                        )}
                      </TypeaheadWrapper>
                    ) : q.field && q.field.type === 'user' ? (
                      <TypeaheadWrapper
                        value={
                          q.value
                            ? { username: q.value, displayName: q.label }
                            : null
                        }
                      >
                        {({ value, setValue }) => (
                          <UserTypeahead
                            value={value}
                            onChange={user => {
                              setValue(user);
                              props.handleFilterValueChange(i)({
                                target: {
                                  value: user ? user.get('username') : '',
                                  label: user
                                    ? user.get('displayName') ||
                                      user.get('username')
                                    : '',
                                },
                              });
                            }}
                            labelProp="displayName"
                            minSearchLength={1}
                            components={{
                              SuggestionContent: UserTypeaheadSuggestionContent,
                            }}
                            aria-labelledby={`filter-group-label-${i}`}
                          />
                        )}
                      </TypeaheadWrapper>
                    ) : (
                      <input
                        type="text"
                        value={props.filterQuery[i].value}
                        onChange={props.handleFilterValueChange(i)}
                        aria-labelledby={`filter-group-label-${i}`}
                      />
                    )}
                    <button
                      className="btn btn-icon-danger"
                      type="button"
                      onClick={props.handleFilterRemoveClick(i)}
                      title="Remove Filter Option"
                    >
                      <span className="fa fa-times" />
                    </button>
                  </div>
                ))}

                {props.addFilterOptions.length > 0 && (
                  <div className="filter-bar__group">
                    <UncontrolledDropdown>
                      <DropdownToggle color="icon" title="Add Filter Option">
                        <span className="fa fa-plus" />
                      </DropdownToggle>
                      <DropdownMenu className="">
                        {props.filterQuery.length === 0 && (
                          <DropdownItem
                            key="any-field"
                            onClick={props.handleFilterAddClick(null)}
                          >
                            <em>
                              <I18n>Any Field</I18n>
                            </em>
                          </DropdownItem>
                        )}
                        {props.addFilterOptions.map(sf => (
                          <DropdownItem
                            key={sf.id}
                            onClick={props.handleFilterAddClick(sf)}
                          >
                            {sf.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                )}

                <div className="filter-bar__group">
                  <button
                    className="btn btn-link"
                    onClick={props.handleFilterSearch}
                    disabled={props.filterQuery.length === 0}
                  >
                    <I18n>Apply</I18n>
                    <span className="fa fa-md fa-arrow-right ml-1" />
                  </button>
                </div>
              </div>
            )}
            <StateListWrapper
              loading={props.loading}
              data={props.data}
              error={props.error}
              emptyTitle={props.emptyTitle}
              emptyMessage={props.emptyMessage}
              errorMessage={true}
            >
              {requests => (
                <>
                  <div className="cards pb-0">
                    {requests.map(request => {
                      return (
                        <RequestCard
                          key={request.id}
                          submission={request}
                          path={getSubmissionPath(
                            props.appLocation,
                            request,
                            null,
                            props.type,
                          )}
                          reloadList={props.reloadRequests}
                          navigate={props.navigate}
                        />
                      );
                    })}
                  </div>
                  <PaginationBar
                    className="mb-3"
                    paging={props.paging}
                    previousPage={
                      props.hasPreviousPage
                        ? props.fetchRequestsPrevious
                        : undefined
                    }
                    nextPage={
                      props.hasNextPage ? props.fetchRequestsNext : undefined
                    }
                    startIndex={props.pageIndexStart}
                    endIndex={props.pageIndexEnd}
                    count={props.count}
                    countDone={props.countDone}
                    pageSize={props.limit}
                    changePageSize={props.handlePageSizeChange}
                  />
                </>
              )}
            </StateListWrapper>
          </div>
        </div>
      </div>
    </div>
  </Fragment>
);

const mapStateToProps = (state, props) => ({
  type: props.type,
  servicesKapp: selectServicesKapp(state),
  appLocation: state.app.location,
  profile: state.app.profile,
  search: state.requests.search,
  limit: state.requests.limit,
  direction: state.requests.direction,
  loading: state.requests.loading,
  data: state.requests.data,
  error: state.requests.error,
  paging: state.requests.paging,
  hasPreviousPage: state.requests.previousPageTokens.size > 0,
  hasNextPage: !!state.requests.nextPageToken,
  pageIndexStart:
    state.requests.previousPageTokens.size * state.requests.limit +
    (state.requests.data && state.requests.data.size > 0 ? 1 : 0),
  pageIndexEnd:
    state.requests.previousPageTokens.size * state.requests.limit +
    ((state.requests.data && state.requests.data.size) || 0),
  count: state.requests.count,
  countDone: state.requests.countDone,
  counts: state.requests.counts,
  isMobile: ['small', 'medium'].includes(state.app.layoutSize),
});

const mapDispatchToProps = {
  fetchRequests: actions.fetchRequestsRequest,
  fetchRequestsPrevious: actions.fetchRequestsPrevious,
  fetchRequestsNext: actions.fetchRequestsNext,
  fetchRequestsCurrent: actions.fetchRequestsCurrent,
  fetchRequestsClear: actions.fetchRequestsClear,
  countRequestsRequest: actions.countRequestsRequest,
};

const initQueryState = query =>
  Array.isArray(query) && query.length > 0
    ? query
    : [{ field: null, value: '' }];

const getEmptyMessaging = (filterOpen, hasSearched, type) => {
  if (filterOpen) {
    if (!hasSearched) {
      return {
        emptyTitle: 'Select filters',
        emptyMessage:
          'Enter filter values and click apply to find matching requests',
      };
    } else {
      return {
        emptyTitle: 'No matching requests',
        emptyMessage: 'Try using a different or less specific filter',
      };
    }
  } else {
    if (type === 'Open') {
      return {
        emptyTitle: 'No open requests',
        emptyMessage: 'Your submitted requests will appear here',
      };
    } else if (type === 'Closed') {
      return {
        emptyTitle: 'No closed requests',
        emptyMessage: 'Your closed requests will appear here',
      };
    } else if (type === 'Draft') {
      return {
        emptyTitle: 'No draft requests',
        emptyMessage: 'Your draft requests will appear here',
      };
    } else if (type === 'OtherOpen') {
      return {
        emptyTitle: 'No open requests',
        emptyMessage: 'There are no submitted requests',
      };
    } else if (type === 'OtherClosed') {
      return {
        emptyTitle: 'No closed requests',
        emptyMessage: 'There are no closed requests',
      };
    } else {
      return {
        emptyTitle: 'No requests found',
      };
    }
  }
};

export const RequestList = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withProps(props => ({
    // Parse type prop to determine coreState and all flag
    coreState: typeStates.get(props.type, C.CORE_STATE_SUBMITTED),
    // The all flag is true if we're searching for others' requests, not just our own
    all: (props.type || '').includes('Other'),
  })),
  // State for defining if filter bar is open or not
  withState(
    'filterOpen',
    'setFilterOpen',
    props => Array.isArray(props.search.query) && props.search.query.length > 0,
  ),
  // Local copy of the filter query array to be used for making changes before searching
  withState('filterQuery', 'setFilterQuery', props =>
    initQueryState(props.search.query),
  ),
  withProps(props => ({
    // Array of additional search fields that can be added to the filter in
    // combination with the currently selected fields
    addFilterOptions: searchFields.filter(sf =>
      props.filterQuery.every(q => q.field && sf.combo.includes(q.field.id)),
    ),
    // Empty message text to show, defined by whether filter is opened and type
    ...getEmptyMessaging(props.filterOpen, !!props.data, props.type),
  })),
  withHandlers({
    // Function to get the available searchFields that the current search field
    // can be changed to. Limited based on other selected fields
    swapFilterOptions: props => field => {
      if (!field || props.filterQuery.length <= 1) {
        return searchFields;
      } else {
        return searchFields.filter(
          sf =>
            sf.id === field.id ||
            props.filterQuery
              .filter(f => f.id !== field.id)
              .every(q => q.field && sf.combo.includes(q.field.id)),
        );
      }
    },
    // Toggles the filter bar open/closed
    toggleFilters: props => () => {
      const isClosing = props.filterOpen;
      props.setFilterOpen(!isClosing);
      if (isClosing) {
        props.setFilterQuery(initQueryState());
        // If closing, fetch the full list for the current type
        props.fetchRequests({
          coreState: props.coreState,
          all: props.all,
        });
      } else {
        // If opening, clear the list to make it easier to tell when the filters are applied
        props.fetchRequestsClear();
      }
    },
    // Updates the field selected in a filter option
    handleFilterFieldChange: props => (index, id) => () => {
      props.setFilterQuery(q => {
        const arr = [...q];
        arr[index] = {
          field: searchFields.find(sf => sf.id === id),
          value: '',
        };
        return arr;
      });
      if (props.data) {
        props.fetchRequestsClear();
      }
    },
    // Updates the value of the filter option
    handleFilterValueChange: props => index => e => {
      const value = e.target.value;
      const label = e.target.label;
      props.setFilterQuery(q => {
        const arr = [...q];
        arr[index] = {
          field: q[index].field,
          value,
          label,
        };
        return arr;
      });
      if (props.data) {
        props.fetchRequestsClear();
      }
    },
    // Removes a filter option
    handleFilterRemoveClick: props => index => () => {
      props.setFilterQuery(q => [...q.slice(0, index), ...q.slice(index + 1)]);
      if (props.data) {
        props.fetchRequestsClear();
      }
    },
    // Adds a filter option
    handleFilterAddClick: props => field => () => {
      props.setFilterQuery(q => [...q, { field, value: '' }]);
      if (props.data) {
        props.fetchRequestsClear();
      }
    },
    // Processes the search after filters have been added
    handleFilterSearch: props => () => {
      if (props.filterQuery.every(q => !!q.value)) {
        props.fetchRequests({
          coreState: props.coreState,
          all: props.all,
          query: props.filterQuery,
        });
      } else {
        // If any filters are missing a value, shows this error instead of searching
        addToast({
          severity: 'warning',
          message: 'All filter options must have a value.',
        });
      }
    },
    // Changes the sort direction and refetches the data
    handleSortDirectionChange: props => direction => () => {
      if (
        direction !== props.direction &&
        ['ASC', 'DESC'].includes(direction)
      ) {
        props.fetchRequests({
          ...props.search.toJS(),
          direction,
        });
      }
    },
    // Changes the page size and refetches the data
    handlePageSizeChange: props => limit => () => {
      if (typeof limit === 'number' && limit > 0 && limit !== props.limit) {
        props.fetchRequests({
          ...props.search.toJS(),
          limit,
        });
      }
    },
  }),
  withHandlers({
    reloadRequests: props => () => {
      if (props.filterOpen) {
        props.handleFilterSearch();
      } else {
        props.fetchRequests();
      }
    },
  }),
  lifecycle({
    componentDidMount() {
      if (
        this.props.search.coreState === this.props.coreState &&
        this.props.search.all === this.props.all
      ) {
        // If type matches the search settings in redux, refetch current page
        this.props.fetchRequestsCurrent();
      } else {
        // Otherwise reset local filter state and fetch the list for the current
        // type without any filters
        this.props.setFilterOpen(false);
        this.props.setFilterQuery(initQueryState());
        this.props.fetchRequests({
          coreState: this.props.coreState,
          all: this.props.all,
        });
      }
    },
    componentDidUpdate(prevProps) {
      if (
        this.props.type !== prevProps.type &&
        (this.props.search.coreState !== this.props.coreState ||
          this.props.search.all !== this.props.all)
      ) {
        // If type is changed and no longer matches the search settings in
        // redux, reset filters and fetch the new list
        this.props.setFilterOpen(false);
        this.props.setFilterQuery(initQueryState());
        this.props.fetchRequests({
          coreState: this.props.coreState,
          all: this.props.all,
        });
      }
    },
  }),
)(RequestListComponent);

/**
 * Wrapper that resets redux state on unmount, allowing all routes within this
 * wrapper to maintain their state, including pagination.
 * Also fetches the counts for all the tabs on mount.
 */
export const RequestListWrapper = compose(
  connect(
    null,
    {
      fetchRequestsReset: actions.fetchRequestsReset,
      countRequestsRequest: actions.countRequestsRequest,
    },
  ),
  lifecycle({
    componentDidMount() {
      this.props.countRequestsRequest();
    },
    componentWillUnmount() {
      this.props.fetchRequestsReset();
    },
  }),
)(props => props.children);
